import * as React from 'react';
import { getMusicKitToken } from '../utils/FirebaseUtils';
import * as Autosuggest from 'react-autosuggest';
import {
  SuggestionsFetchRequestedParams,
  ChangeEvent
} from 'react-autosuggest';
import { storeDispatch } from '../state/Store';
import { addAudioFile } from '../state/actions';
import { trackEvent, reportIssue, reportBFError } from '../utils/Tracker';
import { Overlay } from '../components/Overlay';
import Loop from './Loop';
import { LoopOption } from './LoopOption';

const compileArtworkUrl = (
  url: string,
  width: number,
  height: number
): string => {
  return url.replace('{w}', width + '').replace('{h}', height + '');
};

interface MKResource {
  url: string;
}

interface MKAttributes {
  artistName: string;
  name: string;
  artwork: MKResource;
  previews: MKResource[];
}

interface MKSong {
  id: string;
  attributes: MKAttributes;
}

const createLoopFromSong = (song: MKSong): Loop => {
  return {
    name: song.attributes.name,
    artist: song.attributes.artistName,
    thumb: compileArtworkUrl(song.attributes.artwork.url, 100, 100),
    audio: song.attributes.previews[0].url
  };
};

const getSuggestionValue = (suggestion: any): string => {
  return suggestion;
};

const renderSuggestion = (suggestion: any) => {
  return <span>{suggestion}</span>;
};

interface State {
  query: string;
  suggestions: string[];
  topChart: MKSong[] | null;
  isLoading: boolean;
  results: MKSong[] | null;
  error: string | null;
}

export default class extends React.Component<{}, State> {
  token: string;
  basePath: string = 'https://api.music.apple.com/v1/catalog/us/';
  searchedQuery: string = '';
  input: HTMLInputElement;
  constructor(props: any) {
    super(props);

    this.state = {
      query: '',
      suggestions: [],
      topChart: null,
      isLoading: false,
      results: null,
      error: null
    };
  }

  async fetchMK(url: string): Promise<any> {
    if (!this.token) {
      throw new Error("Can't fetch from MusicKit without token");
    }
    const data = await fetch(this.basePath + url, {
      method: 'get',
      headers: new Headers({
        Authorization: 'Bearer ' + this.token
      })
    });

    if (data.status !== 200) {
      throw new Error('Could not load data');
    }
    return await data.json();
  }

  async loadChart(): Promise<MKSong[]> {
    const result = await this.fetchMK('charts/?types=songs');
    // const firstSong: MKSong = result.results.songs[0].data[0];
    // console.log('first song');
    // console.log(firstSong);
    // const audioUrl = firstSong.attributes.previews[0].url;
    // console.log(audioUrl);
    return result.results.songs[0].data;
  }

  async loadSuggestions(query: string): Promise<string[]> {
    const result = await this.fetchMK(`search/hints?term=${query}`);
    return result.results.terms;
  }

  async loadSearchResults(query: string): Promise<MKSong[]> {
    const result = await this.fetchMK(
      `search?term=${query}&types=songs&limit=20`
    );
    if (result.results.songs) {
      return result.results.songs.data;
    }
    return [];
  }

  componentDidMount() {
    getMusicKitToken()
      .then(token => {
        this.token = token!;
        return this.loadChart();
      })
      .then((topChart: MKSong[]) => {
        this.setState({ topChart }, () => {
          this.input.focus();
        });
      })
      .catch((e: Error) => {
        reportIssue("Couldn't load Token");
        reportBFError(e);
        this.setState({ error: e.message });
      });
  }

  onChange = (event: React.FormEvent<any>, params: ChangeEvent) => {
    this.setState({
      query: params.newValue
    });
  };

  onSuggestionsFetchRequested = (request: SuggestionsFetchRequestedParams) => {
    this.loadSuggestions(request.value)
      .then((suggestions: string[]) => {
        this.setState({
          suggestions
        });
      })
      .catch((e: Error) => {
        reportIssue("Couldn't load suggestions");
        reportBFError(e);
      });
  };

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: []
    });
  };

  searchSongs = (term: string) => {
    if (term === this.searchedQuery) {
      return;
    }
    // console.log('SEARCH SONG: ' + term);
    this.loadSearchResults(term).then((songs: MKSong[]) => {
      this.setState({
        results: songs
      });
    });
    this.searchedQuery = term;
    trackEvent('Search iTunes Song', { query: term });
  };

  onSuggestionSelected = () => {
    this.searchSongs(this.state.query);
  };

  didChooseLoop = async (loop: Loop) => {
    const title = loop.name;
    const artist = loop.artist;
    storeDispatch(addAudioFile(loop.audio, loop.thumb, title, artist));
    trackEvent('Select iTunes Song', { title, artist });
  };

  storeInputReference = (autosuggest: any) => {
    if (autosuggest !== null) {
      this.input = autosuggest.input;
    }
  };

  render() {
    const { query, topChart, results, isLoading, error } = this.state;

    if (error) {
      return (
        <Overlay>
          <h1>{error}</h1>
        </Overlay>
      );
    }

    if (topChart === null || isLoading) {
      return (
        <Overlay>
          <h1>{isLoading ? 'Processing...' : 'Loading...'}</h1>
        </Overlay>
      );
    }

    const inputProps = {
      placeholder: 'Search any song',
      value: query,
      onChange: this.onChange,
      onKeyPress: (e: React.KeyboardEvent<any>) => {
        if (e.key === 'Enter') {
          this.searchSongs(this.state.query);
        }
      }
    };
    const items = results || topChart;
    return (
      <Overlay fullWidth={true}>
        {topChart !== null && (
          <Autosuggest
            suggestions={this.state.suggestions}
            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
            getSuggestionValue={getSuggestionValue}
            renderSuggestion={renderSuggestion}
            inputProps={inputProps}
            onSuggestionSelected={this.onSuggestionSelected}
            ref={this.storeInputReference}
          />
        )}
        <header>
          <h1>{results !== null ? 'Results' : 'Top Chart'}</h1>
        </header>
        {items !== null && (
          <div className="search-itunes-list">
            {items.map(song => {
              const loop = createLoopFromSong(song);
              return (
                <LoopOption
                  loop={loop}
                  key={song.id}
                  onSelect={this.didChooseLoop}
                />
              );
            })}
          </div>
        )}
      </Overlay>
    );
  }
}



// WEBPACK FOOTER //
// ./src/audio-editing/ITunesSearch.tsx